import React, { useEffect, useRef, useState } from 'react';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  ChatLogItem,
  Drive,
  Message,
  MessageBody,
} from '../../../models/interfaces';
import CloseIcon from '@mui/icons-material/Close';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import { Avatar, Box, Button } from '@mui/material';
import Loading from '../Loading';
import {
  chatMessage,
  createChatLog,
  getChatDetail,
} from '../../../services/chat-service';
import { SHARE_PERM_DROPDOWN, STATUS_CODE } from '../../../utils/constants';
import { randomString } from 'utils';

interface IProps {
  chatLogs: ChatLogItem[];
  handleDrawerClose: () => void;
  setChatLog: (chatLog: ChatLogItem[]) => void;
  chatId: string;
  setOpenCopyToClibboard: (open: boolean) => void;
  loading?: boolean;
  chatDetail: Drive | undefined;
}

export default function DrawerChatLog(props: IProps) {
  const {
    chatLogs,
    handleDrawerClose,
    setChatLog,
    chatId,
    setOpenCopyToClibboard,
    loading,
    chatDetail,
  } = props;
  const [inputPrompt, setInputPrompt] = useState('');
  const refChatList = useRef<null | HTMLDivElement>(null);
  const [chatLoading, setChatLoading] = useState(false);

  useEffect(() => {
    if (refChatList && refChatList.current && !loading) {
      refChatList.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [refChatList, chatLogs, loading]);

  const handleChatMessage = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setChatLoading(true);
    const currentMessage = inputPrompt;
    setInputPrompt('');
    const messageInput = {
      role: 'user',
      content: currentMessage,
    };
    setChatLog([
      ...chatLogs,
      {
        id: randomString(10),
        created_at: new Date().toISOString(),
        owner_id: 'user',
        email: '',
        created_by: 'user',
        context: currentMessage,
        status: null,
        chat_id: chatId,
        updated_at: new Date().toISOString(),
      },
    ]);
    await createChatLog(chatId, messageInput);

    const messageBody: MessageBody = {
      model: 'gpt-3.5-turbo',
      temperature: 0.9,
      messages: chatLogs.reduce(function (
        res: Array<Message>,
        obj: ChatLogItem
      ) {
        const role = obj.owner_id !== 'system' ? 'user' : 'system';
        res.push.apply(res, [{ role: role, content: obj.context }]);
        return res;
      },
      []),
    };
    messageBody.messages.push(messageInput);
    const res = await chatMessage(messageBody);
    if (res.status === STATUS_CODE.SUCCESS) {
      const message = res.data.choices[0].message;
      setChatLog([
        ...chatLogs,
        {
          id: '2',
          created_at: new Date().toISOString(),
          owner_id: 'system',
          email: '',
          created_by: 'system',
          context: message.content,
          status: null,
          chat_id: chatId,
          updated_at: new Date().toISOString(),
        },
      ]);
      const newChatLog = await createChatLog(chatId, message);

      if (newChatLog.status === STATUS_CODE.CREATED) {
        const chatDetail = await getChatDetail(chatId);
        if (chatDetail.status === STATUS_CODE.SUCCESS) {
          setChatLog(chatDetail.data);
          setChatLoading(false);
        }
      }
    } else {
      // todo handle error
    }
  };

  const renderChatLog = () => {
    return (
      <div className="list-chat">
        <ul>
          {chatLogs.map((item) => (
            <li
              key={item.id}
              className={`${item.owner_id === 'system' && 'computer-chat'}`}
            >
              <div className="user-info">
                {item.owner_id === 'system' ? (
                  <Avatar
                    className="avatar-icon"
                    alt="LAplust"
                    src="/favicon.ico"
                    variant="square"
                    sx={{ width: 24, height: 24, display: 'flex', mr: 1 }}
                  />
                ) : (
                  <AccountCircleOutlinedIcon className="avatar-icon" />
                )}
                <div>
                  {item.owner_id === 'system' ? 'LAplust Chat' : item.email}
                </div>
              </div>
              <div className="chat-content">
                <div className="message-content">{item.context}</div>
                <div className="message-action">
                  <div className="create-date">{item.updated_at}</div>
                  <div className="action">
                    <ContentCopyIcon
                      sx={{ cursor: 'pointer' }}
                      onClick={() => {
                        setOpenCopyToClibboard(true);
                        navigator.clipboard.writeText(item.context);
                      }}
                    />
                    {/* <DeleteIcon
                      sx={{ cursor: 'pointer' }}
                      onClick={() => onDeleteItem(item.chat_id)}
                    /> */}
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
        {chatLoading && (
          <Box sx={{ position: 'relative' }}>
            <Loading isLoad={chatLoading} />
          </Box>
        )}
        <div ref={refChatList}></div>
      </div>
    );
  };

  const renderNewChatLog = () => {
    return (
      <div className="box-logo">
        <Avatar
          className="logo"
          alt="LAplust"
          src="/favicon.ico"
          variant="square"
          sx={{ width: 72, height: 72, display: 'flex', mr: 1 }}
        />
        <div className="title">LAplust Chat</div>
        <div className="sub-title">何かお手伝いできることはありますか？</div>
      </div>
    );
  };

  return (
    <div className="drawer-keep-action">
      <div className="drawer-header">
        <CloseIcon onClick={handleDrawerClose} className="close-btn" />
      </div>
      <div className="drawer-body">
        <Loading isLoad={loading} />
        {chatLogs && chatLogs?.length ? renderChatLog() : renderNewChatLog()}
      </div>
      {chatDetail && chatDetail.role === SHARE_PERM_DROPDOWN.EDITOR && (
        <div>
          <div className="drawer-footer">
            <form onSubmit={handleChatMessage}>
              <textarea
                className="customize-text-filed"
                rows={5}
                value={inputPrompt}
                onChange={(e) => setInputPrompt(e.target.value)}
                autoFocus
              ></textarea>
              <div className="action-send">
                <Button
                  type="submit"
                  endIcon={<SendRoundedIcon className="send-icon" />}
                  className="send-btn"
                  disabled={!inputPrompt}
                >
                  送信
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
